import React, { useState, useRef, useEffect } from "react";
import Modal from "../Modal";
import Button from "../Button";
import FormInput from "../FormInput";
// Toast is not used here - handled by parent
import { 
  uploadFileWithToken, 
  postData,
  FILE_UPLOAD_V2_URL,
  UPLOAD_VIRTUAL_GRADING_ITEM_URL 
} from "../../utils/api";
import "../../containers/VirtualGrading/VirtualGrading.css";

const UploadModal = ({ isOpen, onClose, onSuccess }) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    make: "",
    model: "",
    year: "",
    partNumber: "",
    description: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const imageFiles = fileArray.filter((file) =>
      file.type.startsWith("image/")
    );

    if (imageFiles.length === 0) {
      setErrors({ images: "Please select only image files" });
      return;
    }

    // Only allow 1 image at a time
    if (imageFiles.length > 1) {
      setErrors({ images: "Only 1 image allowed at a time" });
      return;
    }

    // Replace existing image if any
    const newImage = {
      file: imageFiles[0],
      preview: URL.createObjectURL(imageFiles[0]),
      id: Date.now() + Math.random(),
    };

    setUploadedImages([newImage]);
    setErrors({ ...errors, images: "" });
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeImage = (id) => {
    setUploadedImages((prev) => {
      const updated = prev.filter((img) => img.id !== id);
      if (updated.length === 0) {
        setErrors({ ...errors, images: "" });
      }
      return updated;
    });
    
    // Clear the file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (uploadedImages.length === 0) {
      newErrors.images = "At least one image is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // First, upload the image to get the AWS S3 URL
      let itemURL = "";
      if (uploadedImages.length > 0) {
        const imageUploadResponse = await uploadFileWithToken(
          FILE_UPLOAD_V2_URL,
          uploadedImages[0].file,
          { type: "1" } // Send type parameter as 1
        );
        
        // API returns AWS S3 URL in "imageUrl" key
        itemURL = imageUploadResponse.imageUrl;
        
        if (!itemURL) {
          throw new Error("Failed to upload image - imageUrl not received");
        }
      }

      // Prepare payload according to API specification
      // The imageUrl from upload API is sent as itemURL in the grading API
      const payload = {
        title: formData.title,
        make: formData.make || "",
        model: formData.model || "",
        year: formData.year || "",
        partNo: formData.partNumber || "", // Note: API expects partNo, not partNumber
        description: formData.description || "",
        itemURL: itemURL, // AWS S3 URL from image upload response
      };

      // Submit item data to the grading API
      const response = await postData(UPLOAD_VIRTUAL_GRADING_ITEM_URL, payload);
      
      console.log("Grading API response:", response);

      // Close modal IMMEDIATELY - this is the most important step
      console.log("Closing modal now...");
      if (onClose) {
        console.log("onClose function exists, calling it");
        onClose();
        console.log("onClose called");
      } else {
        console.error("onClose is NOT defined!");
      }
      
      // Reset form state
      setFormData({
        title: "",
        make: "",
        model: "",
        year: "",
        partNumber: "",
        description: "",
      });
      setUploadedImages([]);
      setErrors({});
      setDragActive(false);
      setIsSubmitting(false);
      
      // Clear the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Call onSuccess callback - parent will switch to pending tab
      setTimeout(() => {
        if (onSuccess) {
          console.log("Calling onSuccess with response:", response);
          onSuccess(response);
        }
      }, 150);
    } catch (error) {
      console.error("Upload error:", error);
      console.error("Full error object:", error);
      // Toast error is handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    console.log("Closing modal...");
    setFormData({
      title: "",
      make: "",
      model: "",
      year: "",
      partNumber: "",
      description: "",
    });
    setUploadedImages([]);
    setErrors({});
    setDragActive(false);
    setIsSubmitting(false);
    
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    onClose();
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upload Item for Grading">
      <div className="vg-upload-modal-content">
        <form onSubmit={handleSubmit}>
          {/* Image Upload Area */}
          <div
            className={`vg-dropzone ${dragActive ? "active" : ""} ${
              errors.images ? "error" : ""
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={handleButtonClick}
          >
            <div className="vg-dropzone-content">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                fill="currentColor"
                className="vg-upload-icon"
                viewBox="0 0 16 16"
              >
                <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
                <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
              </svg>
              <h3 className="vg-upload-title">Upload Image</h3>
              <p className="vg-upload-subtitle">
                Drag and drop image here or click to browse
              </p>
              <p className="vg-upload-note">
                1 image only, JPG/PNG format
              </p>
              <button
                type="button"
                className="vg-choose-file-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleButtonClick();
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z" />
                </svg>
                Choose File
              </button>
              {errors.images && (
                <p className="vg-error-message">{errors.images}</p>
              )}
            </div>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            style={{ display: "none" }}
          />

          {/* Image Preview */}
          {uploadedImages.length > 0 && (
            <div className="vg-image-preview-grid">
              {uploadedImages.map((img) => (
                <div key={img.id} className="vg-image-preview">
                  <img src={img.preview} alt="Preview" />
                  <button
                    type="button"
                    className="vg-image-remove"
                    onClick={() => removeImage(img.id)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Form Fields */}
          <div className="vg-form-fields">
            <div className="vg-form-group">
              <label className="vg-form-label">
                Title <span className="vg-required">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`vg-form-input ${errors.title ? "error" : ""}`}
                placeholder="Enter item title"
              />
              {errors.title && (
                <p className="vg-error-message">{errors.title}</p>
              )}
            </div>

            <div className="vg-form-row">
              <div className="vg-form-group">
                <label className="vg-form-label">Make</label>
                <input
                  type="text"
                  name="make"
                  value={formData.make}
                  onChange={handleInputChange}
                  className="vg-form-input"
                  placeholder="e.g., Toyota"
                />
              </div>
              <div className="vg-form-group">
                <label className="vg-form-label">Model</label>
                <input
                  type="text"
                  name="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  className="vg-form-input"
                  placeholder="e.g., Camry"
                />
              </div>
            </div>

            <div className="vg-form-row">
              <div className="vg-form-group">
                <label className="vg-form-label">Year</label>
                <input
                  type="text"
                  name="year"
                  value={formData.year}
                  onChange={handleInputChange}
                  className="vg-form-input"
                  placeholder="e.g., 2020"
                />
              </div>
              <div className="vg-form-group">
                <label className="vg-form-label">Part Number</label>
                <input
                  type="text"
                  name="partNumber"
                  value={formData.partNumber}
                  onChange={handleInputChange}
                  className="vg-form-input"
                  placeholder="e.g., 12345-ABC"
                />
              </div>
            </div>

            <div className="vg-form-group">
              <label className="vg-form-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="vg-form-textarea"
                placeholder="Describe the item condition, any issues, etc."
                rows="3"
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="vg-modal-actions">
            <Button
              type="button"
              className="vg-cancel-button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClose();
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="vg-submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Uploading..." : "Submit for Grading"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default UploadModal;
